import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Vehicle } from "@/types";
import { formatKES, vehicleTitle } from "@/lib/format";
import { CITY_CENTERS, cityCenter } from "@/lib/locations";

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface MapViewProps {
  vehicles: Vehicle[];
  city: string;
  onCityChange: (city: string) => void;
}

export default function MapView({ vehicles, city, onCityChange }: MapViewProps) {
  const [selectedId, setSelectedId] = useState<number | null>(vehicles[0]?.id ?? null);

  useEffect(() => {
    if (vehicles.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!vehicles.some((vehicle) => vehicle.id === selectedId)) {
      setSelectedId(vehicles[0].id);
    }
  }, [vehicles, selectedId]);

  const selected = useMemo(
    () => vehicles.find((vehicle) => vehicle.id === selectedId) ?? null,
    [vehicles, selectedId]
  );

  const center = useMemo(() => {
    if (selected && selected.latitude != null && selected.longitude != null) {
      return { lat: Number(selected.latitude), lng: Number(selected.longitude), zoom: 14 };
    }
    const base = cityCenter(city);
    return { lat: base.lat, lng: base.lng, zoom: 12 };
  }, [selected, city]);

  const mapSrc = `https://www.google.com/maps?q=${center.lat},${center.lng}&z=${center.zoom}&output=embed`;

  const neighbourhoods = useMemo(() => {
    const set = new Set<string>();
    vehicles.forEach((vehicle) => {
      const area = (vehicle.location || "").split(",")[0]?.trim();
      if (area) set.add(area);
    });
    return Array.from(set).slice(0, 8);
  }, [vehicles]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      <div className="order-2 lg:order-1">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {CITY_CENTERS.map((entry) => {
            const active = entry.city.toLowerCase() === city.toLowerCase();
            return (
              <button
                key={entry.city}
                type="button"
                onClick={() => onCityChange(entry.city)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500/12 text-primary-300"
                    : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-500/50"
                }`}
              >
                <i className="ri-map-pin-2-line"></i> {entry.city}
              </button>
            );
          })}
        </div>

        {neighbourhoods.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-community-line"></i> Neighbourhoods:
            </span>
            {neighbourhoods.map((area) => (
              <span key={area} className="rounded-full bg-background-200/60 px-2.5 py-1">
                {area}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 lg:max-h-[640px] lg:overflow-y-auto lg:pr-1">
          {vehicles.map((vehicle) => {
            const image = vehicle.images?.[0]?.url || FALLBACK_IMAGE;
            const title = vehicleTitle(vehicle.make, vehicle.model, vehicle.year);
            const active = vehicle.id === selectedId;
            return (
              <button
                key={vehicle.id}
                type="button"
                onMouseEnter={() => setSelectedId(vehicle.id)}
                onFocus={() => setSelectedId(vehicle.id)}
                onClick={() => setSelectedId(vehicle.id)}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500/10"
                    : "border-background-200 bg-background-100 hover:border-primary-500/50"
                }`}
              >
                <span className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200/60">
                  <img src={image} alt={title} title={`${title} — Gari map listing`} className="h-full w-full object-cover object-top" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-heading text-sm font-semibold text-foreground-950">{title}</span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-foreground-600">
                    <i className="ri-map-pin-line text-foreground-500"></i>
                    <span className="truncate">{vehicle.location || vehicle.city}</span>
                  </span>
                  <span className="mt-1.5 inline-flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-primary-400">
                      <i className="ri-star-fill"></i> {Number(vehicle.rating ?? 0).toFixed(2)}
                    </span>
                    <span className="font-medium text-foreground-900">
                      {formatKES(vehicle.price_per_day)}
                      <span className="font-normal text-foreground-500"> /day</span>
                    </span>
                  </span>
                </span>
                <i className={`shrink-0 text-lg ${active ? "ri-arrow-right-s-line text-primary-500" : "ri-arrow-right-up-line text-foreground-400"}`}></i>
              </button>
            );
          })}
        </div>
      </div>

      <div className="order-1 lg:order-2">
        <div className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-100">
          <div className="relative h-[320px] w-full md:h-[440px] lg:h-[640px]">
            <iframe
              title="Map of available Gari cars"
              src={mapSrc}
              className="h-full w-full border-0"
              style={{ filter: "invert(0.92) hue-rotate(180deg) contrast(0.92)" }}
              referrerPolicy="no-referrer-when-downgrade"
              loading="lazy"
            />
          </div>

          {selected ? (
            <div className="flex flex-col gap-3 border-t border-background-200/70 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-foreground-500">
                  {selected.city}
                  {selected.location ? ` · ${selected.location}` : ""}
                </p>
                <p className="mt-1 truncate font-heading text-base font-semibold text-foreground-950">
                  {vehicleTitle(selected.make, selected.model, selected.year)}
                </p>
                <p className="mt-0.5 text-sm text-foreground-600">
                  {formatKES(selected.price_per_day)} <span className="text-foreground-500">/ day</span>
                </p>
              </div>
              <Link
                to={`/vehicle/${selected.id}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-roadster-line"></i> View this car
              </Link>
            </div>
          ) : (
            <div className="border-t border-background-200/70 p-4">
              <p className="flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-information-line text-primary-500"></i> No cars to pin on the map for this search. Try
                another city or clear your filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}